import React from 'react';
import { Formik, Form } from 'formik';
import type { FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { Stack, Button, Input, Checkbox } from '@/components/ui';
import { getApiErrorMessage, getApiErrorStatus } from '@/utils/apiError';
import type {
  User,
  Company,
  UserGroupInfo,
  CreateUserRequest,
  UpdateUserRequest,
} from '@/types/userManagement.types';

interface UserFormValues {
  name: string;
  email: string;
  whatsAppNumber: string;
  selectedCompanies: number[];
  selectedUserGroups: number[];
  isNotifyMonday: boolean;
  isNotifyTuesday: boolean;
  isNotifyWednesday: boolean;
  isNotifyThursday: boolean;
  isNotifyFriday: boolean;
}

interface UserFormProps {
  user?: User | null;
  companies: Company[];
  userGroups: UserGroupInfo[];
  onSubmit: (data: CreateUserRequest | UpdateUserRequest) => Promise<void>;
  onCancel: () => void;
}

const validationSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  email: Yup.string().email('Please enter a valid email address').required('Email is required'),
  whatsAppNumber: Yup.string().matches(/^\+?[\d\s\-()]+$/, 'Please enter a valid phone number'),
  selectedCompanies: Yup.array(),
  selectedUserGroups: Yup.array(),
});

function CheckRow({
  id,
  checked,
  disabled,
  onChange,
  label,
}: {
  id: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <label
      htmlFor={id}
      className="flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1.5 text-sm text-gray-800 transition-colors hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-dark-700"
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-dark-600 dark:bg-dark-800"
      />
      <span className="min-w-0 leading-snug">{label}</span>
    </label>
  );
}

export const UserForm: React.FC<UserFormProps> = ({
  user,
  companies,
  userGroups,
  onSubmit,
  onCancel,
}) => {
  const getInitialValues = () => ({
    name: user?.name || '',
    email: user?.email || '',
    whatsAppNumber: user?.whatsAppNumber || '',
    selectedCompanies: user?.companies?.map((c) => c.id) ?? [],
    selectedUserGroups: user?.userGroups?.map((g) => g.id) ?? [],
    isNotifyMonday: user?.isNotifyMonday ?? false,
    isNotifyTuesday: user?.isNotifyTuesday ?? false,
    isNotifyWednesday: user?.isNotifyWednesday ?? false,
    isNotifyThursday: user?.isNotifyThursday ?? false,
    isNotifyFriday: user?.isNotifyFriday ?? false,
  });

  const handleFormSubmit = async (
    values: UserFormValues,
    { setFieldError }: FormikHelpers<UserFormValues>
  ) => {
    const submitData: CreateUserRequest | UpdateUserRequest = {
      name: values.name.trim(),
      email: values.email.trim(),
      companyIds: [...new Set(values.selectedCompanies)],
      userGroupIds: [...new Set(values.selectedUserGroups)],
      whatsAppNumber: values.whatsAppNumber.trim() || undefined,
      isNotifyMonday: values.isNotifyMonday,
      isNotifyTuesday: values.isNotifyTuesday,
      isNotifyWednesday: values.isNotifyWednesday,
      isNotifyThursday: values.isNotifyThursday,
      isNotifyFriday: values.isNotifyFriday,
    };

    try {
      await onSubmit(submitData);
    } catch (error) {
      // The page already shows the API's message in a toast; put a duplicate email on the field
      // that caused it and leave the dialog open so the values can be corrected.
      if (getApiErrorStatus(error) === 409) {
        setFieldError('email', getApiErrorMessage(error, 'This email address is already in use'));
      }
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-dark-700 sm:px-5">
        <h2 id="user-form-title" className="type-dialog-title min-w-0">
          {user ? 'Edit user' : 'Add user'}
        </h2>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          className="shrink-0 rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-dark-700 dark:hover:text-gray-200"
          aria-label="Close"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </Button>
      </div>

      <Formik
        initialValues={getInitialValues()}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
        enableReinitialize
      >
        {({ values, setFieldValue, setFieldTouched, errors: formErrors, isSubmitting }) => (
          <Form className="flex min-h-0 flex-1 flex-col">
            <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3 sm:px-5 sm:py-4">
              <Stack spacing={3}>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-4">
                  <Input
                    name="name"
                    label="Name"
                    placeholder="Full name"
                    disabled={isSubmitting}
                    required
                  />
                  <Input
                    name="email"
                    type="email"
                    label="Email"
                    placeholder="email@example.com"
                    disabled={isSubmitting}
                    required
                  />
                </div>

                <Input
                  name="whatsAppNumber"
                  type="tel"
                  label="WhatsApp"
                  placeholder="+1 …"
                  disabled={isSubmitting}
                />

                <fieldset className="min-w-0">
                  <legend className="type-caption mb-2 block font-semibold text-gray-700 dark:text-gray-300">
                    Companies
                  </legend>
                  <div className="max-h-36 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50/80 p-2 dark:border-dark-600 dark:bg-dark-900/40">
                    {companies.length === 0 ? (
                      <p className="px-1 py-2 text-sm text-gray-500 dark:text-gray-400">
                        No companies
                      </p>
                    ) : (
                      <div className="grid grid-cols-1 gap-0.5 sm:grid-cols-2">
                        {companies.map((company) => (
                          <CheckRow
                            key={company.id}
                            id={`company-${company.id}`}
                            checked={values.selectedCompanies.includes(company.id)}
                            disabled={isSubmitting}
                            label={company.name}
                            onChange={(checked) => {
                              const updated = checked
                                ? [...values.selectedCompanies, company.id]
                                : values.selectedCompanies.filter((id) => id !== company.id);
                              setFieldValue('selectedCompanies', updated);
                              setFieldTouched('selectedCompanies', true, false);
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  {formErrors.selectedCompanies && (
                    <p className="mt-1.5 text-xs text-red-600 dark:text-red-400">
                      {formErrors.selectedCompanies}
                    </p>
                  )}
                </fieldset>

                <fieldset className="min-w-0">
                  <legend className="type-caption mb-2 block font-semibold text-gray-700 dark:text-gray-300">
                    User groups
                  </legend>
                  <div className="max-h-36 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50/80 p-2 dark:border-dark-600 dark:bg-dark-900/40">
                    {userGroups.length === 0 ? (
                      <p className="px-1 py-2 text-sm text-gray-500 dark:text-gray-400">
                        No groups
                      </p>
                    ) : (
                      <div className="grid grid-cols-1 gap-0.5 sm:grid-cols-2">
                        {userGroups.map((group) => (
                          <CheckRow
                            key={group.id}
                            id={`group-${group.id}`}
                            checked={values.selectedUserGroups.includes(group.id)}
                            disabled={isSubmitting}
                            label={group.name}
                            onChange={(checked) => {
                              const updated = checked
                                ? [...values.selectedUserGroups, group.id]
                                : values.selectedUserGroups.filter((id) => id !== group.id);
                              setFieldValue('selectedUserGroups', updated);
                              setFieldTouched('selectedUserGroups', true, false);
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  {formErrors.selectedUserGroups && (
                    <p className="mt-1.5 text-xs text-red-600 dark:text-red-400">
                      {formErrors.selectedUserGroups}
                    </p>
                  )}
                </fieldset>

                <fieldset>
                  <legend className="type-caption mb-2 block font-semibold text-gray-700 dark:text-gray-300">
                    Report notifications
                  </legend>
                  <div className="flex flex-wrap gap-x-4 gap-y-2">
                    {Object.entries({
                      isNotifyMonday: 'Mon',
                      isNotifyTuesday: 'Tue',
                      isNotifyWednesday: 'Wed',
                      isNotifyThursday: 'Thu',
                      isNotifyFriday: 'Fri',
                    }).map(([key, label]) => (
                      <Checkbox key={key} name={key} label={label} disabled={isSubmitting} />
                    ))}
                  </div>
                </fieldset>
              </Stack>
            </div>

            <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-gray-200 bg-gray-50/95 px-4 pb-8 pt-4 dark:border-dark-700 dark:bg-dark-900/60 sm:flex-row sm:items-center sm:justify-end sm:px-5 sm:pb-4">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isSubmitting}
                leftIcon={
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                }
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={isSubmitting}
                disabled={isSubmitting}
                leftIcon={
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                }
              >
                {user ? 'Save' : 'Create'}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};
